import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Custom element responsible for rendering recently viewed products.
 * 
 * Loads product data lazily using an IntersectionObserver and fetches rendered
 * HTML using Shopify's search endpoint and the sectionRenderer utility.
 */
if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();
    }

    /**
     * Observer that triggers initialization once the component becomes visible.
     * 
     * @private
     * @type {IntersectionObserver}
     */
    #intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.init();
      },
      { rootMargin: '0px 0px 200px 0px' }
    );

    /**
     * Called when the element is inserted into the DOM.
     * Sets up the intersection observer to lazily initialize the component.
     */
    connectedCallback() {
      this.#intersectionObserver.observe(this);
    }

    /**
     * Called when the element is removed from the DOM.
     * Ensures resources are properly cleaned up.
     */
    disconnectedCallback() {
      if (this.#intersectionObserver) {
        this.#intersectionObserver.disconnect();
      }
    }

    /**
     * Initializes the component by fetching recently viewed product data,
     * rendering the returned markup, filtering items, and injecting them into
     * the .resource-list element.
     * 
     * @async
     * @returns {Promise<void>}
     */
    async init() {
      try {
        const viewedProducts = RecentlyViewed.getProducts();
        if (!Array.isArray(viewedProducts) || viewedProducts.length === 0) {
          this.classList.add('hidden')
          return;
        }

        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
        if (!recentlyViewedMarkup) {
          return;
        }

        this.querySelector(".product-recommendations__skeleton-container")?.remove();

        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(
          recentlyViewedMarkup,
          'text/html'
        );

        if (parsedRecentlyViewedMarkup.querySelector('parsererror')) {
          console.error('Failed to parse recently viewed products markup');
          return;
        }

        const recentlyViewedProductsHtml =
          parsedRecentlyViewedMarkup.querySelector('.product-grid');

        if (!recentlyViewedProductsHtml) {
          return;
        }

        const maxProductsData = this.dataset.max_products;
        let maxProducts = 16;

        if (maxProductsData) {
          const parsed = parseInt(maxProductsData, 10);
          if (!isNaN(parsed) && parsed > 0) {
            maxProducts = parsed;
          }
        }

        const currentProductId = this.dataset.productId;
        if (currentProductId && typeof currentProductId === 'string' && currentProductId.trim()) {
          const productToRemove = recentlyViewedProductsHtml.querySelector(
            `[data-product-id="${CSS.escape(currentProductId)}"]`
          );
          if (productToRemove && productToRemove.parentElement) {
            productToRemove.parentElement.removeChild(productToRemove);
          }
        }

        while (recentlyViewedProductsHtml.children.length > maxProducts) {
          const lastChild = recentlyViewedProductsHtml.lastChild;
          if (lastChild && recentlyViewedProductsHtml.contains(lastChild)) {
            recentlyViewedProductsHtml.removeChild(lastChild);
          }
        }

        const hasSingleItem = recentlyViewedProductsHtml.children.length === 1;
        for (const child of recentlyViewedProductsHtml.children) {
          if (child instanceof HTMLElement) {
            child.setAttribute('data-ref', 'recently-viewed-item');
            child.classList.add('resource-list__item');
          }
          if (hasSingleItem) {
            const height = document.querySelector('.product-card')?.getBoundingClientRect().height;

            if (height > 0) {
              child.style.height = `${height}px`;
            }
          }
        }

        const resourceList = this.querySelector(".resource-list");
        if (resourceList && recentlyViewedProductsHtml.children.length > 0) {
          const fragment = document.createDocumentFragment();
          while (recentlyViewedProductsHtml.children.length > 0) {
            fragment.appendChild(recentlyViewedProductsHtml.children[0]);
          }
          resourceList.prepend(fragment);
        } else {
          this.classList.add('hidden');
        }
      } catch (error) {
        console.error('Error initializing recently viewed products:', error);
      }
    }

    /**
     * Fetches and returns rendered markup for the recently viewed products section.
     * 
     * Uses Shopify search with `id:PRODUCT_ID` queries and the custom view
     * "recently-viewed". Relies on sectionRenderer to retrieve the HTML.
     * 
     * @private
     * @async
     * @returns {Promise<string|null>} The rendered section HTML, or null on failure.
     */
    async #getRecentlyViewedProductsMarkup() {
      try {
        const viewedProducts = RecentlyViewed.getProducts();
        if (!Array.isArray(viewedProducts) || viewedProducts.length === 0) {
          return null;
        }

        if (!Theme?.routes?.search_url) {
          console.warn('Theme.routes.search_url is not available');
          return null;
        }

        const url = new URL(Theme.routes.search_url, location.origin);

        const validIds = viewedProducts
          .filter(id => typeof id === 'string' && id.trim())
          .slice(0, 100);

        if (validIds.length === 0) {
          return null;
        }

        url.searchParams.set(
          'q',
          validIds.map((id) => `id:${encodeURIComponent(id)}`).join(' OR ')
        );
        url.searchParams.set('view', 'recently-viewed');
        url.searchParams.set('resources[type]', 'product');

        if (!sectionRenderer?.getSectionHTML) {
          console.warn('sectionRenderer is not available');
          return null;
        }

        return await sectionRenderer.getSectionHTML('', false, url);
      } catch (error) {
        console.error('Error fetching recently viewed products markup:', error);
        return null;
      }
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
